import { useState, useEffect } from "react";
import {
  X,
  Calendar,
  CheckCircle2,
  Clock3,
  MessageSquare,
  Save
} from "lucide-react";
import { postRequest } from '../../services/apiService'; // Import postRequest
import { toast } from 'react-toastify'; // Import toast for notifications


export default function UpdateTaskINSDAS({ visible, onClose, selectedTask }) {
  // Initialize formData based on selectedTask prop or default empty values
  const [formData, setFormData] = useState({
    status: selectedTask?.status || "In Progress",
    // Ensure percentage is always a number, even if contribution_calc is not.
    percentage: Number(selectedTask?.contribution_calc) || 0,
    startDate: selectedTask?.actual_start?.split('T')[0] || "", // Format date for input type="date"
    endDate: selectedTask?.actual_finish?.split('T')[0] || "",     // Format date for input type="date"
    remarks: selectedTask?.remarks || "",
    project: selectedTask?.project_id || null, // Assuming these IDs are part of selectedTask
    ship: selectedTask?.equipment_id || null,  // Assuming these IDs are part of selectedTask
    task: selectedTask?.id || null,            // The ID of the task itself
  });

  // Update formData when selectedTask changes (i.e., when a new task is selected for update)
  useEffect(() => {
    if (selectedTask) {
      setFormData({
        status: selectedTask.status || "In Progress",
        // Ensure percentage is always a number when selectedTask changes.
        percentage: Number(selectedTask.contribution_calc) || 0,
        startDate: selectedTask.actual_start?.split('T')[0] || "",
        endDate: selectedTask.actual_finish?.split('T')[0] || "",
        remarks: selectedTask.remarks || "",
        project: selectedTask.project_id || null,
        ship: selectedTask.equipment_id || null,
        task: selectedTask.id || null,
      });
    }
  }, [selectedTask]);

  // Function to close the modal, now calls the onClose prop
  const handleClose = () => {
    onClose(); // Call the prop function to close the modal
    console.log("Modal closed!");
  };

  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;

    // Special handling for status change to set percentage to 100
    if (name === 'status' && value === 'Completed') {
      setFormData(prev => ({ ...prev, [name]: value, percentage: 100 }));
    } else if (name === 'percentage') {
      // Convert the value to a number specifically for the percentage field
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    }
    else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        id: formData.task, // Task ID
        status: formData.status,
        contribution_calc: formData.percentage, 
        actual_start: formData.startDate,
        actual_finish: formData.endDate,
        remarks: formData.remarks,
        // Include project_id and equipment_id if needed by the backend for update
        project_id: formData.project,
        // equipment_id: formData.ship,
      };

      console.log("Submitting payload:", payload);
      const response = await postRequest('pert/task-transaction-api/', payload);
      toast.success("Task updated successfully!");
      console.log("Update successful:", response);
      handleClose(); // Close modal on success
    } catch (error) {
      console.error("Error updating task:", error.response?.data || error.message);
      toast.error(`Failed to update task: ${error.response?.data?.detail || error.message}`);
    }
  };


  // Render the component only if 'visible' prop is true
  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 backdrop-blur-sm">
      <div
        className={`bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fadeIn border border-gray-200 w-full max-w-3xl h-5/6`}
      >
        <div className="bg-[#1e3a8a] text-white px-6 py-4 flex justify-between items-center shadow-lg">
          <div>
            <h2 className="text-xl font-bold">Update Task Status</h2>
            <div className="text-sm mt-1 opacity-75">
              Manage completion and details
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleClose} // Call handleClose when the button is clicked
              className="p-2 rounded-lg hover:bg-red-500 hover:bg-opacity-20 transition-all duration-200 transform hover:scale-105"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-y-scroll p-8 bg-gradient-to-br from-white to-gray-50">
            <div className="animate-fadeIn w-full">
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                <h3 className="text-2xl font-bold mb-6 text-gray-800 pb-4 border-b border-gray-200 flex items-center">
                  <CheckCircle2 className="w-6 h-6 mr-3 text-green-600" />
                  {selectedTask?.project_name} - {selectedTask?.ship_name} - {selectedTask?.name}
                </h3>

                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                    <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                      <CheckCircle2 className="w-5 h-5 mr-2 text-green-600" />
                      Status
                    </label>
                    <select
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent shadow-sm bg-white"
                      name="status"
                      value={formData.status}
                      onChange={handleFormChange}
                    >
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Pending">Pending</option>
                      <option value="Delayed">Delayed</option>
                    </select>
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
                    <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                      <Clock3 className="w-5 h-5 mr-2 text-blue-600" />
                      Completion Percentage:
                      <span className="ml-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-bold">
                        {/* Add a check to ensure formData.percentage is a number before calling toFixed */}
                        {typeof formData.percentage === 'number' ? formData.percentage.toFixed(0) : '0'}%
                      </span>
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      name="percentage"
                      value={formData.percentage}
                      onChange={handleFormChange}
                      className="w-full h-3 bg-gradient-to-r from-blue-200 to-purple-200 rounded-lg appearance-none cursor-pointer slider"
                      disabled={formData.status === 'Completed'} // Disable if status is Completed
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2 font-medium">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                      <span>75%</span>
                      <span>100%</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border border-orange-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-orange-600" />
                        Start Date
                      </label>
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleFormChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm bg-white"
                      />
                    </div>
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 p-4 rounded-lg border border-red-200">
                      <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                        <Calendar className="w-5 h-5 mr-2 text-red-600" />
                        End Date
                      </label>
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleFormChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent shadow-sm bg-white"
                      />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                    <label className="block text-gray-700 font-semibold mb-3 flex items-center">
                      <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
                      Remarks
                    </label>
                    <textarea
                      rows="4"
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleFormChange}
                      className="w-full p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none shadow-sm bg-white"
                      placeholder="Add any additional notes or comments here..."
                    />
                  </div>
                </div>
              </div>
            </div>
        </div>

        {/* Action Buttons */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end items-center border-t border-gray-200 shadow-md">
          <button
            type="button"
            onClick={handleClose}
            className="flex items-center px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-100 transition-colors duration-200 shadow-sm mr-4"
          >
            <X className="w-5 h-5 mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit} // Call handleSubmit when save is clicked
            className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 shadow-lg"
          >
            <Save className="w-5 h-5 mr-2" />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}